import { ExternalLink } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import type { AdCampaign } from '../lib/types';

interface AdPlacementProps {
  campaign: AdCampaign;
  variant?: 'strip' | 'card';
}

const ANONYMOUS_KEY = 'hostelgrid_anonymous_id';

function anonymousId(): string {
  try {
    const stored = window.localStorage.getItem(ANONYMOUS_KEY);
    if (stored) return stored;
    const value = typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `anon_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem(ANONYMOUS_KEY, value);
    return value;
  } catch {
    return `anon_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }
}

function safeDestination(value: string): string | null {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : null;
  } catch {
    return null;
  }
}

export function AdPlacement({ campaign, variant = 'strip' }: AdPlacementProps) {
  const elementRef = useRef<HTMLElement | null>(null);
  const impressionSent = useRef(false);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element || impressionSent.current) return;

    const observer = new IntersectionObserver(entries => {
      const visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.5);
      if (!visible || impressionSent.current) return;
      impressionSent.current = true;
      observer.disconnect();
      api.post(`/api/public/ads/${campaign.id}/impression`, {
        anonymousId: anonymousId(),
        path: `${location.pathname}${location.search}`,
      }).catch(() => undefined);
    }, { threshold: [0.5] });

    observer.observe(element);
    return () => observer.disconnect();
  }, [campaign.id]);

  async function openAdvertisement() {
    if (opening) return;
    setOpening(true);
    try {
      const result = await api.post<{ destinationUrl: string }>(`/api/public/ads/${campaign.id}/click`, {
        anonymousId: anonymousId(),
        path: `${location.pathname}${location.search}`,
      });
      const destination = safeDestination(result.destinationUrl);
      if (destination) window.open(destination, '_blank', 'noopener,noreferrer');
    } finally {
      setOpening(false);
    }
  }

  return (
    <article ref={elementRef} className={`ad-placement ad-placement-${variant}`}>
      {campaign.creativeUrl ? <img src={campaign.creativeUrl} alt="" className="ad-placement-image" loading="lazy" decoding="async" /> : null}
      <div className="ad-placement-copy">
        <span className="sponsored-label">Advertisement</span>
        <p className="ad-business-name">{campaign.business_name}</p>
        <h3>{campaign.headline}</h3>
        {campaign.description ? <p>{campaign.description}</p> : null}
      </div>
      <button type="button" className="secondary-button" onClick={openAdvertisement} disabled={opening}>
        {opening ? 'Opening…' : 'Learn more'} <ExternalLink size={15} />
      </button>
    </article>
  );
}
